import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import axiosAPI, { get } from 'utils/axios';
import {
  AUDIT_ENDPOINT_TAG_HISTORY,
  TAG_BY_ID,
  TAG_COUNT_BY_CHILD,
  TAG_LIST,
  TAG_ROOT_COUNT_BY_CHILD,
} from 'config/endpoints';
import { SensorStatus } from 'types/assets.contract';
import * as types from 'types';
import { AssetTags } from 'types/tags.contract';
import { AssetTagsParams, AssetTagsProps, GenericQueryOptions } from './types/tags';

export interface ITags {
  description: string;
  high_high_threshold: number | null;
  high_threshold: number | null;
  id: number;
  low_low_threshold: number | null;
  low_threshold: number | null;
  tag: string;
  type: string;
  unit: string;
}
export interface ITagList {
  tags: ITags[];
}

export enum ETagsQueries {
  UPDATE_TAG = 'tagUpdate',
  DELETE_TAG = 'tagDelete',
  GET_TAG_DETAILS = 'tagDetails',
}

export enum EDateReference {
  START = 'START',
  END = 'END',
}

export type AuditTag = {
  action_timestamp: string;
  id: number;
  new: {
    high_high_threshold?: number;
    high_threshold?: number;
    low_low_threshold?: number;
    low_threshold?: number;
  };
  old: {
    high_high_threshold: null | number;
    high_threshold: null | number;
    low_low_threshold: null | number;
    low_threshold: null | number;
  };
  reason: string;
  table_name: string;
  table_pk: number;
  user_name: string;
};

export type TagHistory = {
  audit: AuditTag[];
  count: number;
};

interface ITagCountSingle {
  id: number;
  name: string;
  tag_summary: {
    total_tag_count: number;
  };
}

interface ITagCount {
  tag_count: {
    child_assets: ITagCountSingle[];
    id: number;
    name: string;
    total_tag_count: number;
  };
}

export const updateTag = async (assetId: string, tagId: number, payload: Partial<SensorStatus>) => {
  const response = await axiosAPI.patch(TAG_BY_ID(assetId, tagId), payload);
  return response;
};

export const deleteTag = async (
  assetId: string | undefined,
  tagId: number | undefined,
): Promise<AxiosResponse> => {
  const response = await axiosAPI.delete(TAG_BY_ID(assetId, tagId), {
    validateStatus: (status) => status >= 200,
  });
  return response;
};

export const useDeleteTag = (assetId: string | undefined, tagId: number | undefined) => {
  return useQuery(['deleteTag'], () => deleteTag(assetId, tagId), {
    enabled: !!assetId && !!tagId,
  });
};

export const usePatchTagsList = (assetId: string | undefined) => {
  return useMutation(({ formData }: { formData: object }) => {
    const response = axiosAPI.patch<types.ValidationErrors | types.ApiResponseErrors>(
      `${TAG_BY_ID(assetId)}`,
      formData,
      {
        validateStatus: (status) => status >= 200,
      },
    );
    return response;
  });
};

const getTagList = async (assetId: string | string | undefined): Promise<ITagList> => {
  const { data } = await axiosAPI.get<ITagList>(TAG_LIST(assetId));
  return data;
};

const fetchAssetTags = ({ assetId, model }: AssetTagsProps) =>
  get<AssetTags, AssetTagsParams>(TAG_LIST(assetId), { model_name: model });

// QUERIES

export const useAssetTagsQuery = (
  { assetId, model }: AssetTagsProps,
  options?: GenericQueryOptions<AssetTags>,
) => useQuery(['ASSET_TAGS', assetId, model], fetchAssetTags({ assetId, model }), options);

export const useGetTagList = (assetId: string | undefined) => {
  return useQuery(['get_tag_list', assetId], () => getTagList(assetId), {
    enabled: !!assetId,
  });
};

const getTagCount = async (assetId: string) => {
  const { data } = await axiosAPI.get<ITagCount>(TAG_COUNT_BY_CHILD(assetId));
  return data;
};

export const useGetTagCount = (assetId: string) => {
  return useQuery(['get_tag_count', assetId], () => getTagCount(assetId), {
    enabled: !!assetId,
  });
};

const getTagRootCount = async () => {
  const { data } = await axiosAPI.get<ITagCount>(TAG_ROOT_COUNT_BY_CHILD);
  return data;
};

export const useGetTagRootCount = (editingMode: boolean) => {
  return useQuery(['get_tag_root_count'], () => getTagRootCount(), { enabled: editingMode });
};

const getTagHistory = async (tagId: number) => {
  const { data } = await axiosAPI.get<TagHistory>(AUDIT_ENDPOINT_TAG_HISTORY(tagId));
  return data;
};

export const useGetTagHistory = (tagId: number) => {
  return useQuery(['tagHistory', tagId], () => getTagHistory(tagId), {
    enabled: !!tagId,
  });
};

export const usePostTagsList = (assetId: string | undefined) => {
  return useMutation(({ formData }: { formData: object }) => {
    const response = axiosAPI.post<types.ValidationErrors | types.ApiResponseErrors>(
      `${TAG_LIST(assetId)}`,
      formData,
      {
        validateStatus: (status) => status >= 200,
      },
    );
    return response;
  });
};
